import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const PREVIEW_MAX = 480;

interface Props {
  url: string | null;
  loading?: boolean;
  aspect?: number; // width / height, for sizing the hover preview
  alt?: string;
  /** Sizing/extra classes for the inline box (e.g. "h-12 w-12" or "h-24 flex-1"). */
  className?: string;
  /** Placeholder text when there is no image and not loading (e.g. "No label"). */
  emptyLabel?: string;
  /** How the image fills the box: whole (the default), or cropped to cover it. The hover preview is always whole. */
  fit?: 'contain' | 'cover';
  /** Where a covering picture is anchored: its middle (the default), or its top — a document's first page shows its heading. */
  position?: 'center' | 'top';
  /** Whether hovering shows the enlarged preview (the default); the study's image cards do without. */
  enlarge?: boolean;
  /** Whether a click (a tap, where nothing hovers) shows and hides the enlarged preview too. */
  toggleOnClick?: boolean;
}

/** An image box that shows an enlarged hover preview (portaled, edge-aware) of the
 *  same image — shared by the worklist and the study's image cards. The image is
 *  natively lazy-loaded; if no aspect is given it's derived from the loaded image. */
export function PreviewThumb({ url, loading, aspect, alt = '', className, emptyLabel, fit = 'contain', position = 'center', enlarge = true, toggleOnClick = false }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);
  const [errored, setErrored] = useState(false);
  const [preview, setPreview] = useState<{ left: number; top: number; w: number; h: number } | null>(
    null,
  );
  // What opened the preview. A tap arrives as mouseenter (hover) and then
  // click: the click keeps what the hover opened instead of closing it, so
  // the preview stays until the next tap or the pointer leaves.
  const openedBy = useRef<'hover' | 'click' | null>(null);

  useEffect(() => setErrored(false), [url]);
  const src = url && !errored ? url : null;

  const show = (by: 'hover' | 'click' = 'hover') => {
    const el = ref.current;
    if (!enlarge || !el || !src) return;
    openedBy.current = by;
    const r = el.getBoundingClientRect();
    const a = aspect ?? naturalAspect ?? 1;
    let w = PREVIEW_MAX;
    let h = PREVIEW_MAX;
    if (a >= 1) h = Math.round(PREVIEW_MAX / a);
    else w = Math.round(PREVIEW_MAX * a);
    const pad = 8;
    let left = r.right + pad;
    if (left + w > window.innerWidth - pad) left = r.left - w - pad;
    let top = r.top + r.height / 2 - h / 2;
    top = Math.max(pad, Math.min(top, window.innerHeight - h - pad));
    setPreview({ left, top, w, h });
  };
  const hide = () => {
    openedBy.current = null;
    setPreview(null);
  };
  const toggle = () => {
    if (preview && openedBy.current === 'click') hide();
    else show('click');
  };
  const toggles = toggleOnClick && !!src;

  return (
    <div
      ref={ref}
      onMouseEnter={() => show('hover')}
      onMouseLeave={hide}
      onClick={
        toggles
          ? (e) => {
              e.stopPropagation();
              toggle();
            }
          : undefined
      }
      onKeyDown={
        toggles
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle();
              } else if (e.key === 'Escape' && preview) {
                e.stopPropagation();
                hide();
              }
            }
          : undefined
      }
      onBlur={toggles ? hide : undefined}
      role={toggles ? 'button' : undefined}
      tabIndex={toggles ? 0 : undefined}
      aria-pressed={toggles ? !!preview : undefined}
      aria-label={toggles ? (alt ? `${alt}: show large` : 'Show large') : undefined}
      className={cn(
        'flex items-center justify-center overflow-hidden rounded border bg-muted/40',
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalHeight > 0) setNaturalAspect(img.naturalWidth / img.naturalHeight);
          }}
          onError={() => setErrored(true)}
          className={cn('h-full w-full', fit === 'cover' ? 'object-cover' : 'object-contain', fit === 'cover' && position === 'top' && 'object-top')}
        />
      ) : loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : emptyLabel ? (
        <span className="px-1 text-center text-[10px] text-muted-foreground/60">{emptyLabel}</span>
      ) : (
        <ImageIcon className="h-4 w-4 text-muted-foreground/50" />
      )}

      {preview &&
        src &&
        createPortal(
          <div
            className="pointer-events-none fixed z-50 overflow-hidden rounded-md border bg-popover p-1 shadow-xl"
            style={{ left: preview.left, top: preview.top }}
          >
            <img
              src={src}
              alt=""
              className="block rounded-sm object-contain"
              style={{ width: preview.w, height: preview.h }}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
